import React, { useEffect, useState } from 'react';
import { useAddVocabulariesMutation, useGetVocabulariesByPersonalContentSetMutation } from '../../../features/authentication/activitySearchApiSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faSearch } from '@fortawesome/pro-solid-svg-icons';
import PcsVocabularyThumbnail from './pcsVocabularyThumbnail';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Button, Dropdown, DropdownButton } from 'react-bootstrap';
import PcsVocabularyPreviewModal from './pcsVocabularyPreviewModal';
import CustomToast from '../../../components/Toast';
import PageNavigation from '../../../components/pageNavigation';
import AddQuestionsToExisting from '../../questionsSearch/components/castleQuestionsSearch/components/questionResults/components/addQuestionsToExisting';
import ActivityCreateContent from '../../activities/components/ActivityCreateContent';
import { DataProvider } from '../../assignments/DataContext';
import ActivityTypes from '../../../enums/activityTypes';
import AddQuestionsToAssignment from '../../questionsSearch/components/addQuestionsToAssignment';

const PersonalContentSetVocabularies = () => {
  const [vocabularies, setVocabularies] = useState([]);
  const [showDefinitions, setShowDefinitions] = useState(true);
  const [goToNumber, setGoToNumber] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [hasNoVocabularies, setHasNoVocabularies] = useState(false);
  const [searchedVocabs, setSearchedVocabs] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [vocabulariesPerPage, setVocabulariesPerPage] = useState(25);
  const [pageCount, setPageCount] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [openOptionTileModal, setOpenOptionTileModal] = useState(false);
  const [dateBasedKey] = useState(new Date().getTime().toString());
  const [getPcsVocabularies, { isLoading: isVocabulariesLoading }] = useGetVocabulariesByPersonalContentSetMutation();
  const [addVocabularies] = useAddVocabulariesMutation();
  const location = useLocation();
  const navigate = useNavigate();
  const {activityId} = useParams();
  const { setId  } = location.state;

  useEffect(() => {
    if(!setId) {
      setHasNoVocabularies(true);
      return;
    }
    getPcsVocabulariesAsync(setId);
  }, [setId]);

  const getPcsVocabulariesAsync = async (setId ) => {
    const options = {
      qsetIds: [setId],
    };
    var response = await getPcsVocabularies(options);
    if(!response.error) {
      if(!response.data.vocabularies || response.data.vocabularies.length == 0) {
        setHasNoVocabularies(true);
      } else {
        const updatedData = response.data.vocabularies.map((item, index) => ({
          ...item, number: index + 1
        }));
        setVocabularies(updatedData);
        setSearchedVocabs(updatedData.slice(0, vocabulariesPerPage));
        setPageCount(Math.ceil(updatedData.length / vocabulariesPerPage));
      }
    }
  };
  
  const performUnifiedSearch = (newSearchTerm, newGoToNumber) => {
    setIsAllSelected(false);

    let results = vocabularies.filter((item, index) => {
      const matchesVocab = newSearchTerm.length === 0 || 
      item.vocabulary?.toLowerCase().includes(newSearchTerm.toLowerCase());
      const matchesNumber = newGoToNumber.length === 0 || 
      (index + 1 === Number(newGoToNumber));
      return matchesVocab && matchesNumber;
    }).map(item => ({ ...item, isSelected: false }));
    setSearchedVocabs(results);
    const updatedSearchedVocabularies = vocabularies.map(item => ({
      ...item, isSelected: false
    }));
    setVocabularies(updatedSearchedVocabularies);
    setSelectedPage(1);
  };

  const handleSetSelectionForAll = () => {
    setIsAllSelected(!isAllSelected);
    const getDisplayVocabs = goToNumber.length === 0 && searchTerm.length === 0 ? vocabularies : searchedVocabs;
    const updatedSearchedVocabs = getDisplayVocabs.map(item => ({
      ...item, isSelected: !isAllSelected
    }));
    const updatedAllVocabs = vocabularies.map(item => {
      const found = updatedSearchedVocabs.find(sv => sv.questionId === item.questionId);
      return found ? found : item;
    });
    setVocabularies(updatedAllVocabs);
    setSearchedVocabs(updatedSearchedVocabs);
  };

  const handleSearchByNumber = (e) => {
    const isValidNumber = /^[0-9]*\.?[0-9]*$/.test(e.target.value);
    if (isValidNumber) {
      setGoToNumber(e.target.value);
      performUnifiedSearch(searchTerm, e.target.value);
    }
  };
  
  const handleSearchByDefinition = (e) => {
    setSearchTerm(e.target.value);
    performUnifiedSearch(e.target.value, goToNumber);
  };

  const handleSetDisplayedVocabularies = (updatedDisplayedVocabularies) => {
    setSearchedVocabs(updatedDisplayedVocabularies);
    setVocabularies((prevVocabularies) =>
      prevVocabularies.map((item) => {
        const updated = updatedDisplayedVocabularies.find((v) => v.questionId === item.questionId);
        return updated ? updated : item;
      })
    );
  };

  const addVocabulariesToFlashcard = async (activityId) => {
    if(!activityId) {
      setOpenOptionTileModal(true);
      return;
    }
    const selectedVocabularies = vocabularies.filter(vocab => vocab.isSelected);
    const selectedVocabulariesList = selectedVocabularies.filter(q => !q.inAssignment).map(vocabulary => ({
      questionId: vocabulary.questionId,
      courseId: vocabulary.courseId
    }));
    var options = {
      id: activityId,
      vocabularies: selectedVocabulariesList
    };
    const response = await addVocabularies(options);
    if(!response.error){
      CustomToast('Selected Vocabularies are added', 'success');
      navigate('/assignments/flashcards');
    }
  };

  const handleSelectPage = (pageNumber) => {
    if (pageNumber == selectedPage) return;
    setSelectedPage(pageNumber);
    const startIndex = (pageNumber - 1) * vocabulariesPerPage;
    const endIndex = startIndex + vocabulariesPerPage;
    setSearchedVocabs(vocabularies.slice(startIndex, endIndex));
  };
  
  const handlePerPageCount = (count) => {
    if (count == vocabulariesPerPage) return;
    setVocabulariesPerPage(count);
    setSearchedVocabs(vocabularies.slice(0, count));
  };
  
  const saveComponentAddToExisting = 
  <AddQuestionsToExisting
    key={'update' + dateBasedKey}
    handleSave={(id) => addVocabulariesToFlashcard(id)}
    handleCancel={() => setOpenOptionTileModal(false)}
    activityType={ActivityTypes.FlashCard} />;
  
  const saveComponentCreate = <DataProvider>
    <ActivityCreateContent
      key={'create' + dateBasedKey}
      showTitle={false}
      fromActivities={false}
      handleCancel={() => setOpenOptionTileModal(false)}
      onCreateSuccess={(id) => addVocabulariesToFlashcard(id)}
    />
  </DataProvider>;

  return (
    <>
      <div className="white-card">
        <div className='d-flex'>
          <div className="w-100"><h5>Browse Vocabularies</h5></div>
        </div>
        <div className='d-flex w-100 align-items-center justify-content-between mb-4'>
          <div>
            <input
              type="checkbox"
              className="form-check-input me-2 cursor-pointer"
              checked={isAllSelected}
              id="allSelected"
              name="allSelectedCheck"
              onChange={() => handleSetSelectionForAll()}
            />
            <label className="form-check-label">
              Select All
            </label>
          </div>
          <div className='align-items-center d-flex justify-content-end gap-2'>
            <div>
              <input
                type="checkbox"
                className="form-check-input me-2 cursor-pointer"
                checked={showDefinitions}
                id="showDefinitions"
                name="showDefinitionsCheck"
                onChange={() => setShowDefinitions(!showDefinitions)}
              />
              <label className="form-check-label">
              Show Definitions
              </label>
            </div>
            <div className="custom-search position-relative">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchByDefinition(e)}
                className="form-control"  
                placeholder='Search Vocab'
              />
            </div>
            <div className='w-33'>
              <div className="d-flex align-items-center gap-2">
                <span className='text-end w-100'>Go to Number</span>
                <input
                  type="text"
                  value={goToNumber}
                  onChange={(e) => handleSearchByNumber(e)}
                  className="form-control"  
                />
              </div>
            </div>
          </div>
        </div>
        {hasNoVocabularies && 
        <div className='noResultsContainer'>
          <div className='noResultsBox'>
            <div className='noResultsIconBox mb-4'>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </div>
            <div className='fw-500 text-secondary'>No vocabularies were found matching your filter(s).  Please try another search.</div>
          </div>
        </div>
        }
        <PcsVocabularyThumbnail
          vocabularies={searchedVocabs}
          setVocabularies={handleSetDisplayedVocabularies}
          isLoading={isVocabulariesLoading}
          showQuestionUsage={true}
          showDefinition={showDefinitions}
          setIsAllSelected={setIsAllSelected}
        />
        <div className='selectionDetailsBar mt-5'>
          <div className='selectionDetailsBar-main'>
            <div className='d-flex flex-row align-items-center selectionDetailsBar-item selectionDetailsBar-left'>
              <span className='fw-bold me-2'>Selected:</span>
              <span className='selectionTypeCount'>{searchedVocabs.filter(item => item.isSelected).length}</span>
            </div>
            <div className='d-flex flex-row flex-wrap align-items-center gap-2 my-2 selectionDetailsBar-item selectionDetailsBar-controls'>
              <span>Vocabularies Per Page:</span>
              <DropdownButton id='dropdown-pageCount-selection' title={vocabulariesPerPage} variant='castleDropdown' drop='down' onSelect={(e) => { handlePerPageCount(Number(e)); }}>
                {[25, 50, 75, 100].map((pageSize) => (
                  <Dropdown.Item 
                    key={pageSize}
                    eventKey={pageSize} 
                    active={vocabulariesPerPage === pageSize}>
                    {pageSize}
                  </Dropdown.Item>
                ))}
              </DropdownButton>
              <PageNavigation handleSelectPage={handleSelectPage} currentPage={selectedPage} pageCount={pageCount} />
              <div className='selectionDetailsBar-item'>
                <Button variant='castleWhite' disabled={searchedVocabs.filter(item => item.isSelected).length === 0} className='btn-width' onClick={()=> setShowPreviewModal(true)}>Preview</Button>
              </div>
              <div className='selectionDetailsBar-item selectionDetailsBar-add'>
                <Button variant='castleRed' disabled={searchedVocabs.filter(item => item.isSelected).length === 0} onClick={() => addVocabulariesToFlashcard(activityId)}>Add Vocabularies</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PcsVocabularyPreviewModal
        vocabularies={searchedVocabs.filter(item => item.isSelected)}
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        addVocabularies={() => addVocabulariesToFlashcard(activityId)}
      />
      {openOptionTileModal &&
        <AddQuestionsToAssignment
          key={'component' + dateBasedKey}
          openOptionTileModal={openOptionTileModal}
          setOpenOptionTileModal={setOpenOptionTileModal}
          saveComponentCreate={saveComponentCreate}
          saveComponentAddToExisting={saveComponentAddToExisting}
          activityType={ActivityTypes.FlashCard}
        />
      }
    </>
  );
};

export default PersonalContentSetVocabularies;